import React, { useState } from 'react';
import { EFFECTS_REGISTRY } from '../registry';
import useTranslation from '../../hooks/useTranslation';
import useEffectNavigation from '../../hooks/useEffectNavigation';
import EffectDrawer from './EffectDrawer';

const EffectSelector = ({ onSelect, onClose, onCategoryOpen, initialCategory, backToCategorySignal }) => {
  const { t } = useTranslation();
  const navigation = useEffectNavigation();

  // Abrir automaticamente a categoria inicial (quando vindo do detalhe)
  React.useEffect(() => {
    if (initialCategory && !navigation.drawerOpen) {
      navigation.navigateToEffects(initialCategory.id, initialCategory.name, initialCategory.effects || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCategory]);

  // Sinal externo para voltar do detalhe para a lista de efeitos da categoria
  React.useEffect(() => {
    if (backToCategorySignal) {
      navigation.navigateBackToEffects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backToCategorySignal]);

  // Função para navegar para lista de efeitos de uma categoria
  const handleCategoryClick = (categoryId, categoryData) => {
    const categoryName = t(`categories.${categoryId}`);
    navigation.navigateToEffects(categoryId, categoryName, categoryData.effects);
    if (onCategoryOpen) {
      onCategoryOpen({ id: categoryId, name: categoryName, effects: categoryData.effects });
    }
  };

  // Categorizar efeitos
  const categorizedEffects = {
    basic: {
      effects: ['rainDrops', 'blueNoise']
    },
    enhancement: {
      effects: ['sharpenFactor']
    },
    blur: {
      effects: ['gaussianBlur', 'hatchBlur', 'zoomBlur', 'ditherBlur'],
      illustration: '/assets/effects/blur/GaussianBlur.webp'
    },
    artistic: {
      effects: ['traceEdges', 'sketch', 'bwSketch', 'sketchBook', 'bwSketchNew', 'drawingPractice2', 'crossHatchNatron', 'dropsScreen', 'mysticGrid', 'desenho99'],
      illustration: '/assets/effects/artistic/Drawing.webp'
    },
    cores: {
      effects: ['multicolor', 'shadowColor', 'neon'],
      illustration: '/assets/effects/cores/colors.webp'
    },
    printing: {
      effects: ['newspapersDithering', 'halftone', 'halftoneCmykCartoon', 'cmykSplit', 'dotDamme', 'ditherBayer', 'asciiArt', 'asciiArtColor'],
      illustration: '/assets/effects/printing/impressao.webp'
    },
    mosaicos: {
      effects: ['tiledMosaic', 'mosaic5', 'leadlightMosaic'],
      illustration: '/assets/effects/mosaicos/mosaico.webp'
    },
    arte: {
      effects: ['oilPaintBrush', 'painted', 'pixelTransform', 'dawnBringer', 'pixelArtPalette', 'watercolor'],
      illustration: '/assets/effects/arte/tokio.webp'
    },
    lens: {
      effects: ['lensFlare', 'lensFlareOnly', 'anamorphicFlare', 'lensFlareStrong', 'lensFlareFun'],
      illustration: '/assets/effects/lens/foresta.webp'
    },
    particles: {
      effects: ['magicParticles', 'magicParticlesV2']
    },
    animation: {
      effects: ['plasmaWaves', 'circlePlasma', 'rainScreen', 'rainDrops']
    },
    distortion: {
      effects: ['chromaticAberration', 'crtTv', 'blockGlitch', 'distorcaoCaotica']
    },
    other: {
      effects: ['distorcaoPolar', 'dotRipples'] // Efeitos diversos em "Outros"
    }
  };

  // Obter todos os IDs categorizados
  const categorizedIds = new Set([
    ...categorizedEffects.basic.effects,
    ...categorizedEffects.enhancement.effects,
    ...categorizedEffects.blur.effects,
    ...categorizedEffects.artistic.effects,
    ...categorizedEffects.cores.effects,
    ...categorizedEffects.printing.effects,
    ...categorizedEffects.mosaicos.effects,
    ...categorizedEffects.arte.effects,
    ...categorizedEffects.lens.effects,
    ...categorizedEffects.particles.effects,
    ...categorizedEffects.animation.effects,
    ...categorizedEffects.distortion.effects,
    ...categorizedEffects.other.effects
  ]);

  // Preencher "Outros" com efeitos não categorizados
  Object.keys(EFFECTS_REGISTRY).forEach(id => {
    if (!categorizedIds.has(id)) {
      categorizedEffects.other.effects.push(id);
    }
  });

  // Componente de categoria - Agora apenas botão para navegar
  const CATEGORY_FALLBACK_NAMES = {
    basic: 'Básicos',
    enhancement: 'Melhorias',
    blur: 'Blur',
    artistic: 'Desenho',
    cores: 'Cores',
    printing: 'Impressão',
    mosaicos: 'Mosaicos',
    arte: 'Arte',
    lens: 'Reflexo e Lente',
    particles: 'Partículas',
    animation: 'Animação',
    distortion: 'Distorção',
    other: 'Outros'
  };
  const CategoryButton = ({ categoryId, categoryData }) => {
    const effects = categoryData.effects
      .map(id => EFFECTS_REGISTRY[id])
      .filter(Boolean);

    if (effects.length === 0) return null;

    const illustration = categoryData.illustration;
    // Categorias que devem ter fundo sem imagem
    const categoriesWithoutImage = ['basic', 'particles', 'animation', 'distortion', 'other'];
    const shouldShowImage = illustration && !categoriesWithoutImage.includes(categoryId);
    
    const nameKey = `categories.${categoryId}`;
    const translated = t(nameKey);
    const displayName = translated && !String(translated).startsWith('categories.')
      ? translated
      : (CATEGORY_FALLBACK_NAMES[categoryId] || categoryId);

    return (
      <button
        onClick={() => handleCategoryClick(categoryId, categoryData)}
        className="w-full px-3 py-2.5 flex items-center justify-between transition-colors group hover:opacity-90"
      >
        <div className="flex items-center w-full">
          {shouldShowImage && (
            <div className="relative w-full">
              <img 
                src={illustration} 
                alt="" 
                className="w-full object-cover border" 
                style={{ 
                  borderColor: 'rgba(255,255,255,0.1)',
                  height: '102px',
                  minHeight: '102px',
                  borderRadius: '12px'
                }} 
              />
              <div className="absolute inset-x-0 bottom-0 px-3 py-1 bg-gradient-to-t from-black/72 to-transparent flex items-center justify-between" style={{ borderRadius: '0 0 12px 12px' }}>
                <span className="font-semibold text-white drop-shadow" style={{ fontSize: '13px' }}>
                  {displayName}
                </span>
              </div>
            </div>
          )}
          {!shouldShowImage && (
            <div className="w-full flex items-center justify-center" style={{ 
              height: '102px',
              minHeight: '102px',
              backgroundColor: '#28380f',
              borderRadius: '12px'
            }}>
              <h4 className="text-sm font-bold text-white" style={{ fontSize: '13px' }}>
                {displayName}
              </h4>
            </div>
          )}
        </div>
      </button>
    );
  };

  return (
    <div className="flex flex-col h-full relative">
      {/* Header */}
      <div className={`p-3 border-b transition-transform duration-300 ease-in-out ${
        navigation.drawerOpen ? '-translate-x-full' : 'translate-x-0'
      }`} style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="flex items-center">
          <button
            onClick={onClose}
            className="p-1 rounded transition-colors hover:opacity-90"
            title={t('common.back')}
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Lista de Categorias */}
      <div className={`flex-1 overflow-y-auto transition-transform duration-300 ease-in-out ${
        navigation.drawerOpen ? '-translate-x-full' : 'translate-x-0'
      }`}>
        <CategoryButton categoryId="basic" categoryData={categorizedEffects.basic} />
        <CategoryButton categoryId="enhancement" categoryData={categorizedEffects.enhancement} />
        <CategoryButton categoryId="blur" categoryData={categorizedEffects.blur} />
        <CategoryButton categoryId="artistic" categoryData={categorizedEffects.artistic} />
        <CategoryButton categoryId="cores" categoryData={categorizedEffects.cores} />
        <CategoryButton categoryId="printing" categoryData={categorizedEffects.printing} />
        <CategoryButton categoryId="mosaicos" categoryData={categorizedEffects.mosaicos} />
        <CategoryButton categoryId="arte" categoryData={categorizedEffects.arte} />
        <CategoryButton categoryId="lens" categoryData={categorizedEffects.lens} />
        <CategoryButton categoryId="particles" categoryData={categorizedEffects.particles} />
        <CategoryButton categoryId="animation" categoryData={categorizedEffects.animation} />
        <CategoryButton categoryId="distortion" categoryData={categorizedEffects.distortion} />
        <CategoryButton categoryId="other" categoryData={categorizedEffects.other} />
      </div>

      {/* Drawer de Efeitos */}
      <EffectDrawer
        isOpen={navigation.drawerOpen}
        onClose={navigation.navigateBackToCategories}
        onSelectEffect={(effectId) => {
          navigation.navigateToEffectDetails(effectId);
          onSelect(effectId);
        }}
        categoryId={navigation.selectedCategory?.id}
        categoryName={navigation.selectedCategory?.name}
        effects={navigation.selectedCategory?.effects || []}
        direction={navigation.direction}
      />
    </div>
  );
};

export default EffectSelector;
